import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
    AuctionCatalogsService,
    InvoiceDetail,
    ListCards200Response,
    PaymentProfileCardsResponse,
} from '@viewbid/ts-angular-client';
import { VbSnackbarService } from '@viewbid/ui';
import {
    isCardExpired,
    isCardExpiringSoon,
} from 'src/app/viewbid/shared/utils/payment-utils';

/** Typographic card-brand chip (design §Step 3) — mono label on brand color. */
interface CardBrandChip {
    label: string;
    color: string;
}

@Component({
    selector: 'vb-invoice-payment',
    templateUrl: './invoice-payment.component.html',
    styleUrls: ['./invoice-payment.component.scss'],
})
export class InvoicePaymentComponent implements OnInit, OnChanges {
    @Input() invoiceDetails: InvoiceDetail | null = null;
    @Input() isStepTwoComplete: boolean = false;
    @Input() scheduleLater: boolean = false;
    @Input() selectedDeliveryMethod: string | null = null;
    @Input() isStepThreeComplete: boolean = false;
    paymentMethods: PaymentProfileCardsResponse[] = [];
    @Input() selectedPaymentCard: PaymentProfileCardsResponse | null = null;
    @Input() isPayOnPickup: boolean = false;
    @Output() isStepThreeCompleteChange: EventEmitter<boolean> =
        new EventEmitter<boolean>();
    @Output()
    selectedPaymentCardChange: EventEmitter<PaymentProfileCardsResponse | null> =
        new EventEmitter<PaymentProfileCardsResponse | null>();
    @Output() isPayOnPickupChange: EventEmitter<boolean> =
        new EventEmitter<boolean>();

    private static readonly BRAND_CHIPS: Record<string, CardBrandChip> = {
        visa: { label: 'VISA', color: '#1a1f71' },
        mastercard: { label: 'MC', color: '#eb5b25' },
        americanexpress: { label: 'AMEX', color: '#016fd0' },
        discover: { label: 'DISC', color: '#e55c20' },
    };

    constructor(
        private router: Router,
        private auctionCatalogsService: AuctionCatalogsService,
        private snackbarService: VbSnackbarService,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        this.getPaymentMethods();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['scheduleLater']) {
            this.scheduleLater = changes['scheduleLater'].currentValue;
        }
        if (changes['invoiceDetails']) {
            this.invoiceDetails = changes['invoiceDetails'].currentValue;
        }
        if (changes['selectedDeliveryMethod']) {
            this.selectedDeliveryMethod =
                changes['selectedDeliveryMethod'].currentValue;
        }
        if (changes['isStepTwoComplete']) {
            this.isStepTwoComplete = changes['isStepTwoComplete'].currentValue;
        }
        if (changes['isStepThreeComplete']) {
            this.isStepThreeComplete =
                changes['isStepThreeComplete'].currentValue;
        }
        if (changes['isPayOnPickup']) {
            this.isPayOnPickup = changes['isPayOnPickup'].currentValue;
        }
        if (changes['selectedPaymentCard']) {
            this.selectedPaymentCard =
                changes['selectedPaymentCard'].currentValue;
        }
    }

    /** Pay-on-pickup only exists on the pickup branches, never for shipping
     *  or in schedule-later (nothing is being collected in person yet). */
    get showPayOnPickup(): boolean {
        return (
            this.selectedDeliveryMethod !== 'shipping' && !this.scheduleLater
        );
    }

    isExpired(card: PaymentProfileCardsResponse): boolean {
        return isCardExpired(card.expirationDate);
    }

    isExpiringSoon(card: PaymentProfileCardsResponse): boolean {
        return isCardExpiringSoon(card.expirationDate);
    }

    cardTag(card: PaymentProfileCardsResponse): string | null {
        if (this.isExpired(card)) return 'Expired';
        if (this.isExpiringSoon(card)) return 'Expires soon';
        return null;
    }

    brandChip(card: PaymentProfileCardsResponse): CardBrandChip | null {
        const type: string = (card.cardType ?? '').toLowerCase();
        return InvoicePaymentComponent.BRAND_CHIPS[type] ?? null;
    }

    selectCard(card: PaymentProfileCardsResponse): void {
        if (this.isExpired(card)) return;
        this.selectedPaymentCard = card;
        this.isPayOnPickup = false;
        this.selectedPaymentCardChange.emit(this.selectedPaymentCard);
        this.isPayOnPickupChange.emit(false);
    }

    selectPayOnPickup(): void {
        this.isPayOnPickup = true;
        this.selectedPaymentCard = null;
        this.selectedPaymentCardChange.emit(null);
        this.isPayOnPickupChange.emit(true);
    }

    private async getPaymentMethods(): Promise<void> {
        await this.auctionCatalogsService.listCards().subscribe({
            next: (response: ListCards200Response) => {
                this.paymentMethods =
                    response.data as PaymentProfileCardsResponse[];
            },
            error: () => {
                this.snackbarService.error(
                    this.translate.instant('Payments.cardListLoadFailed'),
                );
            },
        });
    }

    goToAddCard(): void {
        this.router.navigate(['profiles/my-profile/add-payment-information']);
    }

    onPaymentNextClick(): void {
        this.isStepThreeComplete = true;
        this.isStepThreeCompleteChange.emit(this.isStepThreeComplete);
    }
}
